/**
 * Pure section-splice + body-assembler helpers for the ARA report.
 *
 * The backend wraps each section as
 *   <section id="KEY" class="ara-section" data-section-key="KEY">…</section>
 * (see `apps/ara/app/views/report.py` `section_html` / `role_sections_html`).
 * These helpers rebuild `body_html` for `/ara/render` after applied edits and
 * swap a single section in-place inside an already-rendered document string.
 */

/**
 * Join section HTML into a single `body_html` string, in `section_order` order.
 * Keys in `section_order` that are absent from `sections` are skipped; sections
 * not listed in `section_order` are omitted. The result is a flat concatenation
 * (no separator), matching how the backend assembles the report body.
 *
 * Section numbers are positional: each non-summary section's `.sec-num` is
 * rewritten to its order index, so a chat-inserted custom section landing
 * mid-document renumbers everything after it. Documents whose numbers already
 * match are untouched (the fast path returns the input string), so previously
 * saved bodies stay byte-identical.
 */
export function assembleBodyHtml(sections: Record<string, string>, sectionOrder: string[]): string {
  let out = ''
  // The backend numbers narrative sections excluding the role summary (which
  // stays numberless), so the summary key must not consume a position.
  let position = 0
  for (const key of sectionOrder) {
    const html = sections[key]
    if (!html) continue
    const numbered = key === 'summary' ? html : renumberSectionNum(html, position++)
    out += stripEditChrome(numbered)
  }
  return out
}

/** The role summary's number stays empty by design — it is not part of the
 * numbered sequence. */
const SECTION_NUM_SUMMARY_MARK = 'ara-summary'

function renumberSectionNum(html: string, index: number): string {
  if (html.includes(SECTION_NUM_SUMMARY_MARK)) return html
  const num = String(index + 1).padStart(2, '0')
  if (html.includes(`<span class="sec-num">${num}</span>`)) return html
  const container = document.createElement('div')
  container.innerHTML = html
  const numEl = container.querySelector('.sec-num')
  if (!numEl) return html
  numEl.textContent = num
  return container.innerHTML
}

/**
 * Class tokens the injected report script adds to the live DOM for editing and
 * must never leak into saved or published HTML. Mirrors
 * `EDIT_ARTIFACT_CLASS_TOKENS` in `apps/ara/app/views/report.py`.
 */
const EDIT_ARTIFACT_CLASS_TOKENS = [
  'ara-picker-selected',
  'ara-picker-inset',
  'ara-picker-hover',
  'ara-picker-col',
  'ara-editable-leaf'
] as const

/**
 * Remove editor chrome (picker/outline classes, `contenteditable`, injected
 * action buttons) from a section HTML fragment. The injected script already
 * cleans on `sectionEdit` capture, but sections persisted by older builds can
 * still carry the attributes — and `/ara/render` bakes whatever `body_html`
 * it receives into the published snapshot. Every render/publish path funnels
 * through here, so previously-damaged sections heal on the next save.
 */
export function stripEditChrome(html: string): string {
  if (!EDIT_ARTIFACT_CLASS_TOKENS.some(token => html.includes(token)) && !html.includes('contenteditable')) {
    return html
  }
  const container = document.createElement('div')
  container.innerHTML = html
  container
    .querySelectorAll('.ara-target-actions, .sec-edit-btn')
    .forEach(node => node.remove())
  container.querySelectorAll('[class]').forEach(el => {
    const className = el.getAttribute('class') ?? ''
    const tokens = className.split(/\s+/).filter(Boolean)
    const kept = tokens.filter(token => !(EDIT_ARTIFACT_CLASS_TOKENS as readonly string[]).includes(token))
    if (kept.length !== tokens.length) {
      if (kept.length === 0) el.removeAttribute('class')
      else el.setAttribute('class', kept.join(' '))
    }
  })
  container
    .querySelectorAll('[contenteditable]')
    .forEach(el => el.removeAttribute('contenteditable'))
  return container.innerHTML
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Replace the single `<section … data-section-key="key" …>…</section>` block
 * inside `renderedDoc` with `newHtml`. Only the matching section is replaced;
 * every other section (and all surrounding content) is left byte-for-byte
 * intact. A missing key is a deterministic no-op — the document is returned
 * unchanged.
 *
 * The match is anchored on the `data-section-key="key"` attribute so keys that
 * are substrings of other keys (e.g. `overview` vs `overview_summary`) don't
 * collide. Section content is assumed not to nest `<section>` tags, matching
 * the backend's wrappers.
 */
export function spliceSection(renderedDoc: string, key: string, newHtml: string): string {
  if (!key) return renderedDoc
  const escaped = escapeRegExp(key)
  const re = new RegExp(`<section[^>]*data-section-key="${escaped}"[^>]*>[\\s\\S]*?</section>`, '')
  const match = renderedDoc.match(re)
  if (!match || match.index === undefined) return renderedDoc
  return (
    renderedDoc.slice(0, match.index) + newHtml + renderedDoc.slice(match.index + match[0].length)
  )
}

/**
 * The subset of a scoped `AraEditTarget` needed to locate a single element
 * inside a section's HTML. Mirrors `resolveTarget` in the `apps/ara` injected
 * script (`static/index.html`).
 */
export type SpliceTargetAnchor = {
  key?: string
  selector?: string
  sectionKey?: string
  id?: string
  type?: string
  /** Edit-group name (`data-edit-group`) when the target is a component.
   * Group containers carry no `data-edit-key`/DOM id, so this is the only
   * reliable handle; `id` (`group:<section>:<n>`) disambiguates same-named
   * groups by their document-order occurrence (FE-6079). */
  group?: string
  /** Synthetic column hint; the spliced element is still the enclosing group. */
  column?: string
}

function cssEscape(value: string): string {
  const scope = globalThis as { CSS?: { escape?: (value: string) => string } }
  return scope.CSS?.escape ? scope.CSS.escape(value) : value
}

/**
 * Resolve a `data-edit-group` container. Several groups may share a human name,
 * so the index id (`group:<section>:<n>`, mirrored by the backend's target
 * index) supplies the document-order occurrence; without a parseable n the
 * first match wins. Returns null when no group with that name exists.
 */
function resolveGroupElement(root: ParentNode, anchor: SpliceTargetAnchor): Element | null {
  const name = anchor.group ?? ''
  const anyGroup = '[data-edit-group]'
  const scopedSelector = anchor.sectionKey
    ? `.ara-section[data-section-key="${cssEscape(anchor.sectionKey)}"] ${anyGroup}`
    : anyGroup
  let groups = Array.from(root.querySelectorAll(scopedSelector))
  if (groups.length === 0 && anchor.sectionKey) {
    // Unwrapped storage (raw body without the `.ara-section` wrapper): accept a
    // plain match only when this fragment is not a DIFFERENT section's wrapper.
    const wrapperKey = root.querySelector('.ara-section[data-section-key]')?.getAttribute(
      'data-section-key'
    )
    if (!wrapperKey || wrapperKey === anchor.sectionKey) {
      groups = Array.from(root.querySelectorAll(anyGroup))
    }
  }
  const parts = (anchor.id ?? '').split(':')
  const occurrence = Number.parseInt(parts[parts.length - 1] ?? '', 10)
  // The backend numbers every `data-edit-group` in the section regardless of
  // name, so the occurrence indexes the unfiltered list.
  const byOccurrence =
    Number.isInteger(occurrence) && occurrence > 0 ? groups[occurrence - 1] : undefined
  if (byOccurrence?.getAttribute('data-edit-group') === name) return byOccurrence
  return groups.find(group => group.getAttribute('data-edit-group') === name) ?? null
}

function resolveTargetElement(root: ParentNode, anchor: SpliceTargetAnchor): Element | null {
  let target: Element | null = null
  if (anchor.key) target = root.querySelector(`[data-edit-key="${cssEscape(anchor.key)}"]`)
  if (!target && anchor.selector) {
    try {
      target = root.querySelector(anchor.selector)
    } catch {
      target = null
    }
  }
  if (!target && anchor.group) {
    target = resolveGroupElement(root, anchor)
  }
  if (!target && anchor.id) target = root.querySelector(`#${cssEscape(anchor.id)}`)

  const sectionKey =
    anchor.sectionKey ?? (anchor.type === 'section-body' ? anchor.key : undefined) ?? anchor.key
  if (!target && anchor.type === 'diagram' && sectionKey) {
    const scoped = `.ara-section[data-section-key="${cssEscape(sectionKey)}"]`
    target =
      root.querySelector(`${scoped} .mermaid[data-edit-key]`) ??
      root.querySelector(`${scoped} .mermaid`)
  }
  if (!target && sectionKey && (anchor.type === 'section' || anchor.type === 'section-body')) {
    const scoped = `.ara-section[data-section-key="${cssEscape(sectionKey)}"]`
    target = root.querySelector(`${scoped} .ara-section-body`) ?? root.querySelector(scoped)
  }
  return target
}

function replaceResolvedTarget(
  target: Element,
  anchor: SpliceTargetAnchor,
  newHtml: string
): void {
  if (
    !newHtml.trim() &&
    (anchor.type === 'section' || anchor.type === 'section-body')
  ) {
    target.closest('section.ara-section[data-section-key]')?.remove()
    return
  }

  if (target.classList.contains('ara-section')) {
    const header = target.querySelector('.sec-header')
    target.innerHTML = (header ? header.outerHTML : '') + newHtml
    return
  }
  target.outerHTML = newHtml
}

/**
 * Replace a single element inside a section's HTML with `newHtml`, resolving the
 * element by `data-edit-key`, then `selector`, `id`, and finally `sectionKey`
 * fallbacks (mermaid `.mermaid`, then `.ara-section-body`, then the section).
 * When nothing resolves the input is returned unchanged (deterministic no-op).
 *
 * Sibling to `spliceSection`: that swaps a whole `<section>` inside the rendered
 * document; this swaps one element inside a single section's HTML — used to
 * apply an element-scoped `target_edit`.
 */
export function spliceTarget(
  sectionHtml: string,
  anchor: SpliceTargetAnchor,
  newHtml: string
): string {
  const container = document.createElement('div')
  container.innerHTML = sectionHtml
  const target = resolveTargetElement(container, anchor)
  if (!target) return sectionHtml
  replaceResolvedTarget(target, anchor, newHtml)
  return container.innerHTML
}

/**
 * Resolve `anchor` against every section in `sectionOrder` and splice `newHtml`
 * into the first section that contains it, returning the updated sections and
 * `matched: true`. When no section owns the target — hero/KPI/footer/nav live in
 * the render template, not in any section body — `matched` is `false` and the
 * caller routes the edit to `structured_ara.text_overrides` instead.
 *
 * This replaces keying the splice on `anchor.sectionKey` alone: the iframe
 * picker omits `sectionKey` for keyed template/title elements, so a single
 * `sections[sectionKey]` lookup silently no-ops (`sections[undefined] === ''`).
 */
export function spliceTargetAcrossSections(
  sections: Record<string, string>,
  sectionOrder: string[],
  anchor: SpliceTargetAnchor,
  newHtml: string
): { sections: Record<string, string>; matched: boolean } {
  for (const key of sectionOrder) {
    const sectionHtml = sections[key]
    if (!sectionHtml) continue
    const container = document.createElement('div')
    container.innerHTML = sectionHtml
    const target = resolveTargetElement(container, anchor)
    if (!target) continue
    replaceResolvedTarget(target, anchor, newHtml)
    return { sections: { ...sections, [key]: container.innerHTML }, matched: true }
  }
  return { sections, matched: false }
}

/** Plain-text content of an HTML fragment, for `text_overrides` (the render
 * template escapes overrides to text via `_text_override`). */
export function extractTargetText(html: string): string {
  const container = document.createElement('div')
  container.innerHTML = html
  return (container.textContent ?? '').trim()
}

/**
 * Mirror inline `textEdit` overrides into the stored section HTML. `/ara/render`
 * only honours `text_overrides` for template chrome (hero, footer); sections
 * ship as pre-rendered `body_html`, and the `sectionEdit` capture covers only
 * `.ara-section-body` — so a keyed element in the section header (title,
 * eyebrow, description) would otherwise lose its edit on the next render.
 * Values land as `textContent`, matching the iframe's plain-text contract.
 */
export function applyTextOverridesToSections(
  sections: Record<string, string>,
  overrides: Record<string, string>
): Record<string, string> {
  const keys = Object.keys(overrides)
  if (keys.length === 0) return sections
  let next = sections
  for (const [sectionKey, sectionHtml] of Object.entries(sections)) {
    if (!sectionHtml || !keys.some(key => sectionHtml.includes(key))) continue
    const container = document.createElement('div')
    container.innerHTML = sectionHtml
    let changed = false
    for (const key of keys) {
      for (const el of container.querySelectorAll(`[data-edit-key="${cssEscape(key)}"]`)) {
        el.textContent = overrides[key]
        changed = true
      }
    }
    if (changed) next = { ...next, [sectionKey]: container.innerHTML }
  }
  return next
}

/**
 * Merge an edited section BODY back into its wrapped `<section>`, preserving the
 * `<section>` element (class, `data-section-key`) and its `.sec-header` (number +
 * title). `wrappedSection` is the stored `<section …>…</section>`; `newBodyHtml`
 * is the `.ara-section-body` innerHTML the injected report script emits on a
 * `sectionEdit` — the body only, without the section wrapper or header.
 *
 * Storing `newBodyHtml` directly into `edit.sections[key]` drops the wrapper and
 * the title (the `.ara-section-body` is a runtime-only div, absent from the
 * stored source), so on the next `/ara/render` the section loses its `<section>`
 * container and heading. Re-wrapping keeps the report structure intact.
 */
export function replaceSectionBody(wrappedSection: string, newBodyHtml: string): string {
  const container = document.createElement('div')
  container.innerHTML = wrappedSection
  const section = container.querySelector('section.ara-section[data-section-key]')
  if (!section) return newBodyHtml
  const header = section.querySelector(':scope > .sec-header')
  section.innerHTML = (header ? header.outerHTML : '') + newBodyHtml
  return section.outerHTML
}

/**
 * Split an assembled role body (the `role` frame's `html`, produced by
 * `role_sections_html`) into its per-section wrapped fragments, keyed by
 * `data-section-key` and preserving DOM order.
 *
 * The generation stream emits each `section_frame` RAW (no `.ara-section`
 * wrapper), so building the doc from those fragments yields an unselectable,
 * uneditable report. The assembled `role` frame is already wrapped — splitting
 * it back into the sections map gives every downstream consumer (picker,
 * `spliceTargetAcrossSections`, section-scoped chat edits) the `.ara-section`
 * wrappers they require, including the role `summary` section which is not a
 * picker section but must survive re-render.
 *
 * `order` is the authoritative section order taken from the assembled DOM, so
 * re-renders reproduce the backend's own numbering and summary placement.
 */
export function splitRoleHtmlIntoSections(html: string): {
  sections: Record<string, string>
  order: string[]
} {
  const container = document.createElement('div')
  container.innerHTML = html
  const sections: Record<string, string> = {}
  const order: string[] = []
  for (const section of container.querySelectorAll('section.ara-section[data-section-key]')) {
    const key = section.getAttribute('data-section-key')
    if (!key || key in sections) continue
    sections[key] = section.outerHTML
    order.push(key)
  }
  return { sections, order }
}
